import React from 'react';
import { Drill, Ruler, Truck, Laptop, Box, AlertTriangle, User, CheckCircle, PenTool } from 'lucide-react';
import type { Tool } from '@shared/types/inventory';
import type { Technician } from '@shared/types';

interface ToolCardProps {
    tool: Tool;
    technician?: Technician; // The assigned technician if any
    onAssign: (tool: Tool) => void;
    onEdit: (tool: Tool) => void;
}

const getCategoryIcon = (category: Tool['category']) => {
    switch (category) {
        case 'Electroportatif': return <Drill size={24} />;
        case 'Mesure': return <Ruler size={24} />;
        case 'Vehicule': return <Truck size={24} />;
        case 'Informatique': return <Laptop size={24} />;
        case 'Echelles': return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v20" /><path d="M14 2v20" /><path d="M6 6h12" /><path d="M6 10h12" /><path d="M6 14h12" /><path d="M6 18h12" /></svg>; // DNA/Ladder lookalike
        default: return <Box size={24} />;
    }
};

const getStatusColor = (status: Tool['status']) => {
    switch (status) {
        case 'available': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
        case 'assigned': return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
        case 'maintenance': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
        case 'lost': return 'bg-red-500/20 text-red-500 border-red-500/30';
        default: return 'bg-gray-500/20 text-gray-500';
    }
};

const getStatusLabel = (status: Tool['status']) => {
    switch (status) {
        case 'available': return 'Disponible';
        case 'assigned': return 'Assigné';
        case 'maintenance': return 'Maintenance';
        case 'lost': return 'Perdu/Volé';
    }
};

export const ToolCard: React.FC<ToolCardProps> = ({ tool, technician, onAssign, onEdit }) => {
    return (
        <div className="glass-panel p-4 flex flex-col group relative hover:border-primary/50 transition-colors">
            {/* Top Section: Icon/Image */}
            <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getStatusColor(tool.status)} bg-opacity-10`}>
                    {tool.image ? (
                        <img src={tool.image} alt={tool.name} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                        getCategoryIcon(tool.category)
                    )}
                </div>
                <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(tool.status)}`}>
                    {getStatusLabel(tool.status)}
                </div>
            </div>

            {/* Info */}
            <div className="flex-grow mb-4">
                <h3 className="font-bold text-main line-clamp-1" title={tool.name}>{tool.name}</h3>
                <div className="flex items-center gap-2 text-xs text-muted mb-1">
                    <span className="bg-glass-border/30 px-1.5 py-0.5 rounded">{tool.category}</span>
                    {tool.inventoryNumber && (
                        <span className="bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded font-mono">
                            {tool.inventoryNumber}
                        </span>
                    )}
                    {tool.serialNumber && <span className="text-[10px] text-gray-500 opacity-60">S/N: {tool.serialNumber}</span>}
                </div>

                {/* Assignment Info */}
                {tool.status === 'assigned' && (
                    <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-glass-border/20 border border-glass-border/50">
                        {technician ? (
                            <>
                                <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                                    style={{ backgroundColor: technician.color }}
                                >
                                    {technician.name.charAt(0)}
                                </div>
                                <span className="text-xs font-medium text-main">{technician.name}</span>
                            </>
                        ) : (
                            <span className="text-xs text-muted italic">Tech inconnu ({tool.assignedTo})</span>
                        )}
                    </div>
                )}
                {tool.status === 'maintenance' && tool.description && (
                    <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                        <AlertTriangle size={12} className="inline mr-1" />
                        {tool.description}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="border-t border-glass-border pt-3 flex items-center justify-between gap-2">
                <button
                    onClick={() => onEdit(tool)}
                    className="p-2 rounded-lg hover:bg-glass-border/20 text-muted hover:text-main transition-colors"
                    title="Modifier"
                >
                    <PenTool size={16} />
                </button>

                {tool.status === 'available' ? (
                    <button
                        onClick={() => onAssign(tool)}
                        className="flex-1 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 text-xs font-bold transition-all flex items-center justify-center gap-1"
                    >
                        <User size={14} />
                        Assigner
                    </button>
                ) : tool.status === 'assigned' ? (
                    <button
                        onClick={() => onAssign(tool)} // Re-assigning or un-assigning logic to be handled up top
                        className="flex-1 py-1.5 rounded-lg bg-glass-border/20 hover:bg-glass-border/30 text-muted border border-glass-border text-xs font-medium transition-all flex items-center justify-center gap-1"
                    >
                        <CheckCircle size={14} />
                        Restituer
                    </button>
                ) : (
                    <div className="flex-1"></div>
                )}
            </div>
        </div>
    );
};
